// =============================================================================
// setterPerformanceCacheService
// =============================================================================
// PURPOSE: localStorage caching for Setter Performance tab on DM Analytics.
//
// ARCHITECTURE:
// - Reuses the same CacheEntry pattern as dmCacheService (org-scoped, TTL-based)
// - Stale-while-revalidate: hooks show cached data instantly, refresh in background
// - Cache keys include date range hash to avoid stale results on range change
//
// TTLs:
// - Setter IDs: 10 min (roster rarely changes)
// - Metrics: 5 min (stable within a date range)
// - Proof: 5 min (stable within date range + metric type)
// - Messages: 5 min (conversation history rarely changes)
// =============================================================================

import { logger } from "./logger";
import { CACHE_VERSION } from "./cacheVersion";
import { toOrgDateKey } from "./timezone";

// Cache prefix (separate namespace from dmCacheService)
const CACHE_PREFIX = `altara_${CACHE_VERSION}_sp_`;

// TTL constants (milliseconds) — "live" ranges (includes today)
const SETTER_IDS_TTL = 10 * 60 * 1000; // 10 minutes
const METRICS_TTL = 5 * 60 * 1000; // 5 minutes
const PROOF_TTL = 5 * 60 * 1000; // 5 minutes
export const MESSAGES_TTL = 5 * 60 * 1000; // 5 minutes
const DAILY_BREAKDOWN_TTL = 5 * 60 * 1000; // 5 minutes
const SESSION_TIMELINE_TTL = 5 * 60 * 1000; // 5 minutes

// Historical data (date range ends before today) never changes — cache for 24 hours
const HISTORICAL_TTL = 24 * 60 * 60 * 1000;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  orgId: string;
}

// ─── Smart TTL: historical data lives much longer ────────────────────────────

/**
 * If the cache key contains a date range that ends before today,
 * the data is frozen (historical) and uses HISTORICAL_TTL instead.
 * Key format: ..._YYYY-MM-DD_YYYY-MM-DD or ..._YYYY-MM-DD_YYYY-MM-DD_...
 */
function getEffectiveTTL(baseTTL: number, key: string): number {
  const dateMatch = key.match(/(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})/);
  if (!dateMatch) return baseTTL;

  const endDate = new Date(dateMatch[2] + "T23:59:59");
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // End date is before today → historical, immutable data
  if (endDate < today) {
    return Math.max(baseTTL, HISTORICAL_TTL);
  }

  return baseTTL;
}

// ─── In-Memory Cache Layer ───────────────────────────────────────────────────
// Primary cache — survives CACHE_VERSION bumps, can't overflow, instant reads.
// localStorage is the secondary/persistence layer.
const memoryCache = new Map<string, { data: unknown; timestamp: number; orgId: string }>();

// ─── Fetch Coordination ─────────────────────────────────────────────────────
// Shared promises so hooks can await an in-flight prefetch instead of duplicating.
// Key = same as cache key (e.g. "metrics_orgId_YYYY-MM-DD_YYYY-MM-DD").
// The prefetch registers a promise; hooks check before starting their own fetch.
const pendingFetches = new Map<string, Promise<unknown>>();

export function getPendingFetch<T>(key: string): Promise<T> | null {
  const pending = pendingFetches.get(CACHE_PREFIX + key);
  return pending ? (pending as Promise<T>) : null;
}

export function setPendingFetch(key: string, promise: Promise<unknown>): void {
  const fullKey = CACHE_PREFIX + key;
  pendingFetches.set(fullKey, promise);
  // Auto-cleanup when done (success or failure)
  promise.finally(() => {
    if (pendingFetches.get(fullKey) === promise) {
      pendingFetches.delete(fullKey);
    }
  });
}

// ─── Core Get/Set ────────────────────────────────────────────────────────────

function getCache<T>(key: string, ttl: number, orgId: string): T | null {
  // Check in-memory first (instant, survives CACHE_VERSION bumps)
  const memKey = CACHE_PREFIX + key;
  const mem = memoryCache.get(memKey);
  if (mem && mem.orgId === orgId) {
    const effectiveTTL = getEffectiveTTL(ttl, key);
    if (Date.now() - mem.timestamp <= effectiveTTL) {
      return mem.data as T;
    }
    memoryCache.delete(memKey);
  }

  // Fall back to localStorage
  try {
    const raw = localStorage.getItem(memKey);
    if (!raw) return null;

    const entry: CacheEntry<T> = JSON.parse(raw);
    const effectiveTTL = getEffectiveTTL(ttl, key);
    const isExpired = Date.now() - entry.timestamp > effectiveTTL;
    const isSameOrg = entry.orgId === orgId;

    if (isExpired || !isSameOrg) {
      localStorage.removeItem(memKey);
      return null;
    }

    // Promote to memory cache for instant access next time
    memoryCache.set(memKey, { data: entry.data, timestamp: entry.timestamp, orgId });
    return entry.data;
  } catch {
    return null;
  }
}

function setCache<T>(key: string, data: T, orgId: string): void {
  const memKey = CACHE_PREFIX + key;

  // Always write to memory (instant, can't fail)
  memoryCache.set(memKey, { data, timestamp: Date.now(), orgId });

  // Best-effort write to localStorage (may fail on quota)
  try {
    const entry: CacheEntry<T> = { data, timestamp: Date.now(), orgId };
    localStorage.setItem(memKey, JSON.stringify(entry));
  } catch {
    clearOldCaches();
    logger.warn("SP cache write failed, cleared old entries (memory cache still active)");
  }
}

function clearOldCaches(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove
      .slice(0, Math.ceil(keysToRemove.length / 2))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // Ignore cleanup errors
  }
}

// ─── Date Range Hash ─────────────────────────────────────────────────────────

function dateHash(dateRange: { start: Date | null; end: Date | null }): string {
  if (!dateRange.start || !dateRange.end) return "none";
  return `${toOrgDateKey(dateRange.start)}_${toOrgDateKey(dateRange.end)}`;
}

// ─── Setter IDs ──────────────────────────────────────────────────────────────

export function getSetterIdsCache(orgId: string): string[] | null {
  return getCache<string[]>(`setter_ids_${orgId}`, SETTER_IDS_TTL, orgId);
}

export function setSetterIdsCache(orgId: string, ids: string[]): void {
  setCache(`setter_ids_${orgId}`, ids, orgId);
}

// ─── Setter Metrics ──────────────────────────────────────────────────────────

export function getMetricsCache<T>(
  orgId: string,
  dateRange: { start: Date | null; end: Date | null }
): T | null {
  return getCache<T>(`metrics_${orgId}_${dateHash(dateRange)}`, METRICS_TTL, orgId);
}

export function setMetricsCache<T>(
  orgId: string,
  dateRange: { start: Date | null; end: Date | null },
  data: T
): void {
  setCache(`metrics_${orgId}_${dateHash(dateRange)}`, data, orgId);
}

// ─── Proof Data ──────────────────────────────────────────────────────────────

export function getProofCache<T>(
  orgId: string,
  dateRange: { start: Date | null; end: Date | null },
  metricType: string
): T | null {
  return getCache<T>(`proof_${orgId}_${dateHash(dateRange)}_${metricType}`, PROOF_TTL, orgId);
}

export function setProofCache<T>(
  orgId: string,
  dateRange: { start: Date | null; end: Date | null },
  metricType: string,
  data: T
): void {
  setCache(`proof_${orgId}_${dateHash(dateRange)}_${metricType}`, data, orgId);
}

// ─── AI Summaries ───────────────────────────────────────────────────────────

const SUMMARIES_TTL = 5 * 60 * 1000; // 5 minutes

export function getSummariesCache<T>(conversationId: string, orgId: string): T | null {
  return getCache<T>(`summaries_${conversationId}`, SUMMARIES_TTL, orgId);
}

export function setSummariesCache<T>(conversationId: string, orgId: string, data: T): void {
  setCache(`summaries_${conversationId}`, data, orgId);
}

// ─── Conversation Messages ───────────────────────────────────────────────────

export function getMessagesCache<T>(conversationId: string, orgId: string): T | null {
  return getCache<T>(`messages_${conversationId}`, MESSAGES_TTL, orgId);
}

export function setMessagesCache<T>(conversationId: string, orgId: string, data: T): void {
  setCache(`messages_${conversationId}`, data, orgId);
}

// ─── Daily Breakdown ─────────────────────────────────────────────────────────

export function getDailyBreakdownCache<T>(
  orgId: string,
  dateRange: { start: Date | null; end: Date | null },
  setterId: string
): T | null {
  return getCache<T>(
    `daily_breakdown_${orgId}_${dateHash(dateRange)}_${setterId}`,
    DAILY_BREAKDOWN_TTL,
    orgId
  );
}

export function setDailyBreakdownCache<T>(
  orgId: string,
  dateRange: { start: Date | null; end: Date | null },
  setterId: string,
  data: T
): void {
  setCache(`daily_breakdown_${orgId}_${dateHash(dateRange)}_${setterId}`, data, orgId);
}

// ─── Session Timeline ───────────────────────────────────────────────────────

export function getSessionTimelineCache<T>(
  orgId: string,
  dateRange: { start: Date | null; end: Date | null },
  setterId: string
): T | null {
  return getCache<T>(
    `session_timeline_${orgId}_${dateHash(dateRange)}_${setterId}`,
    SESSION_TIMELINE_TTL,
    orgId
  );
}

export function setSessionTimelineCache<T>(
  orgId: string,
  dateRange: { start: Date | null; end: Date | null },
  setterId: string,
  data: T
): void {
  setCache(`session_timeline_${orgId}_${dateHash(dateRange)}_${setterId}`, data, orgId);
}

// ─── Daily Rows (for client-side aggregation) ───────────────────────────────
// Fetch the full month of raw daily rows once, slice any sub-range client-side.
// Keyed by org + month (e.g., "2026-03"). 10-min TTL for live months, 24h for historical.

const DAILY_ROWS_TTL = 10 * 60 * 1000; // 10 minutes

export function getDailyRowsCache<T>(orgId: string, monthKey: string): T | null {
  return getCache<T>(`daily_rows_${orgId}_${monthKey}`, DAILY_ROWS_TTL, orgId);
}

export function setDailyRowsCache<T>(orgId: string, monthKey: string, data: T): void {
  setCache(`daily_rows_${orgId}_${monthKey}`, data, orgId);
}

// ─── Setter Names (GHL ID → display name) ───────────────────────────────────

export function getSetterNamesCache(orgId: string): Record<string, string> | null {
  return getCache<Record<string, string>>(`setter_names_${orgId}`, SETTER_IDS_TTL, orgId);
}

export function setSetterNamesCache(orgId: string, names: Record<string, string>): void {
  setCache(`setter_names_${orgId}`, names, orgId);
}

// ─── Grid Messages (bulk cache for ProofGridView) ───────────────────────────

const GRID_MESSAGES_TTL = 5 * 60 * 1000; // 5 minutes

export function getGridMessagesCache<T>(idsKey: string, orgId: string): T | null {
  // Use a hash of the idsKey to keep localStorage key length manageable
  const hash =
    idsKey.length > 100 ? `${idsKey.length}_${idsKey.slice(0, 50)}_${idsKey.slice(-50)}` : idsKey;
  return getCache<T>(`grid_msgs_${hash}`, GRID_MESSAGES_TTL, orgId);
}

export function setGridMessagesCache<T>(idsKey: string, orgId: string, data: T): void {
  const hash =
    idsKey.length > 100 ? `${idsKey.length}_${idsKey.slice(0, 50)}_${idsKey.slice(-50)}` : idsKey;
  setCache(`grid_msgs_${hash}`, data, orgId);
}
